import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

/**
 * SuggestedChangesPanel
 *
 * Renders the SuggestedChanges JSONB stored on a feedback row and lets the
 * user trigger / regenerate an AI analysis. Suspect nodes / synapses / gates
 * are surfaced with deep links into the existing graph editor pages so the
 * user can manually apply the change.
 */
function SuggestedChangesPanel({ feedback, graphId, onUpdated }) {
  const [suggestions, setSuggestions] = useState(feedback?.suggested_changes || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSuggestions(feedback?.suggested_changes || null);
    setError(null);
  }, [feedback?.feedback_id, feedback?.suggested_changes]);

  const navigate = useNavigate();

  const generate = async () => {
    if (!feedback?.feedback_id || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.generateFeedbackSuggestions(feedback.feedback_id, { use_llm: true });
      if (res?.success) {
        setSuggestions(res.suggestions || res.feedback?.suggested_changes || null);
        if (onUpdated && res.feedback) {
          onUpdated(res.feedback);
        }
      } else {
        setError(res?.error || 'Could not generate suggestions');
      }
    } catch (err) {
      console.error('Failed to generate suggestions:', err);
      setError(err?.response?.data?.error || err.message || 'Could not generate suggestions');
    } finally {
      setLoading(false);
    }
  };

  const openNodesEditor = () => {
    navigate(`/user-home/graphs/build/${graphId}/build-tune/change-nodes-edges`);
  };

  const hasSuggestions = !!suggestions && (
    !!suggestions.summary
    || (suggestions.suspect_nodes && suggestions.suspect_nodes.length)
    || (suggestions.suspect_synapses && suggestions.suspect_synapses.length)
    || (suggestions.suspect_gates && suggestions.suspect_gates.length)
    || (suggestions.recommendations && suggestions.recommendations.length)
  );

  return (
    <div className="suggested-changes-panel">
      <div className="suggested-changes-header">
        <div>
          <div className="suggested-changes-eyebrow">AI Suggested Changes</div>
          {suggestions?.generated_at && (
            <div className="suggested-changes-meta">
              Generated {new Date(suggestions.generated_at).toLocaleString()}
              {suggestions.model && ` · ${suggestions.model}`}
            </div>
          )}
        </div>
        <div className="suggested-changes-actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={generate}
            disabled={loading}
          >
            {loading
              ? 'Analyzing…'
              : hasSuggestions ? 'Regenerate' : 'Suggest Fixes'}
          </button>
        </div>
      </div>

      {error && <div className="suggested-changes-error">{error}</div>}

      {!hasSuggestions && !loading && !error && (
        <div className="suggested-changes-empty">
          No analysis yet. Click <strong>Suggest Fixes</strong> to inspect the execution trace
          for likely-suspect nodes, synapses, and gates based on this feedback.
        </div>
      )}

      {hasSuggestions && (
        <>
          {suggestions.summary && (
            <div className="suggested-changes-summary">{suggestions.summary}</div>
          )}

          {suggestions.suspect_nodes?.length > 0 && (
            <SuggestionList
              title="Suspect nodes"
              items={suggestions.suspect_nodes.map((n) => ({
                id: n.node_id,
                primary: n.node_name || n.node_id,
                secondary: n.reason,
                badge: n.role,
              }))}
              onAction={openNodesEditor}
              actionLabel="Open in editor"
            />
          )}

          {suggestions.suspect_synapses?.length > 0 && (
            <SuggestionList
              title="Suspect connections"
              items={suggestions.suspect_synapses.map((s) => ({
                id: s.synapse_id,
                primary: `${s.source_name || s.source_id || '?'} → ${s.target_name || s.target_id || '?'}`,
                secondary: s.reason,
                badge: s.weight != null ? `w=${Number(s.weight).toFixed(2)}` : null,
              }))}
              onAction={openNodesEditor}
              actionLabel="Open in editor"
            />
          )}

          {suggestions.suspect_gates?.length > 0 && (
            <SuggestionList
              title="Suspect gates"
              items={suggestions.suspect_gates.map((g) => ({
                id: g.gate_id,
                primary: g.name || g.gate_id,
                secondary: g.reason,
                badge: g.fired ? 'fired' : 'blocked',
              }))}
              onAction={openNodesEditor}
              actionLabel="Open in editor"
            />
          )}

          {suggestions.recommendations?.length > 0 && (
            <div className="suggested-changes-block">
              <div className="suggested-changes-block-title">Recommendations</div>
              <ol className="suggested-changes-recs">
                {suggestions.recommendations.map((rec, idx) => (
                  <li key={idx} className="suggested-changes-rec">
                    <div className="rec-action">{formatAction(rec.action)}</div>
                    <div className="rec-rationale">{rec.rationale}</div>
                    {(rec.node_id || rec.synapse_id || rec.gate_id) && (
                      <div className="rec-target muted">
                        {rec.node_id && `node:${rec.node_id} `}
                        {rec.synapse_id && `synapse:${rec.synapse_id} `}
                        {rec.gate_id && `gate:${rec.gate_id}`}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SuggestionList({ title, items, onAction, actionLabel }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="suggested-changes-block">
      <div className="suggested-changes-block-title">{title}</div>
      <ul className="suggested-changes-list">
        {items.map((it, idx) => (
          <li key={it.id || idx} className="suggested-changes-list-item">
            <div className="item-primary">
              {it.primary}
              {it.badge && <span className="item-badge">{it.badge}</span>}
            </div>
            {it.secondary && <div className="item-secondary">{it.secondary}</div>}
          </li>
        ))}
      </ul>
      {onAction && (
        <button type="button" className="btn-link" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function formatAction(action) {
  if (!action) return 'Action';
  return action
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default SuggestedChangesPanel;
